"use client";

import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import { pitchGetter } from "@/app/(application)/(normal)/create/components/input/pitch";
import { pitchGetterFollowup } from "@/app/(application)/(normal)/create/components/input/pitchGetterFollowup";
import { pitchShiftMiddleware } from "@/app/(application)/(normal)/create/components/input/pitchShiftMiddleware";
import { sessionSlugGetter } from "@/app/(application)/(normal)/create/components/input/sessionSluggetter";
import { vibratoMiddleware } from "@/app/(application)/(normal)/create/components/input/vibratoMiddleware";
import { Button } from "@/components/buttons/Button";
import { InvalidLengthEditModal } from "@/components/fileCoverInput/inputMethods/InvalidLengthEditModal";
import { AutotuneModal } from "@/components/pitchCorrection/AutotuneModal";
import { SECONDS_MAX_EDIT_IN_PITCH_EDITOR } from "@/constants/constants";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { useNamedHubertization } from "@/lib/zustand/useHubertization";
import { useNamedKey } from "@/lib/zustand/useKey";
import { useNamedPitchCorrection } from "@/lib/zustand/usePitchCorrection";
import {
  Artist,
  Instrument,
  MixedModelConstructedNoDates,
  Model,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { getPresignedUrlModelMixedModel } from "@/utils/artistInstrumentModelMixedModel/get/getPresignedUrlModelMixedModel";
import { isSelectedFile } from "@/utils/selectedFileCover/is/isSelectedFile";
import { semitonesToFrequency } from "@/utils/semitonesToFrequency";
import { isFreeUser } from "@/utils/subscriptionLevels/isFreeUser";

export function AutoTuneButton({
  user,
  namespace,
  track,
  hubertizationNamespace,
  keyNamespace,
  conversionNamespace,
  disableDiatonicTranspose,
  artistInstrumentModelMixedModel,
  disabledExternal,
  className,
  buttonLabel = "Pitch editor",
}: {
  user: UserNoDates | null;
  namespace: string;
  track: number;
  hubertizationNamespace: string;
  keyNamespace: string;
  conversionNamespace: string;
  disableDiatonicTranspose: boolean;
  artistInstrumentModelMixedModel:
    | Artist
    | Instrument
    | ModelNoDates
    | MixedModelConstructedNoDates
    | null;
  disabledExternal: boolean;
  className?: string;
  // as the button is pulling double-duty now in both input and the harmony maker we give the optional bits here
  buttonLabel?: string;
}) {
  // There are now different namespaces - one for the local pitch correction , one for the input in the harmony maker, and one for the root hubertization
  const usePitchCorrection = useNamedPitchCorrection((state) =>
    state.getNamedStore(namespace)
  );

  const useHubertization = useNamedHubertization((state) =>
    state.getNamedStore(hubertizationNamespace)
  );

  const useConversionInput = useAudioInput((state) =>
    state.getNamedStore(conversionNamespace)
  );

  const pitches = usePitchCorrection((state) => state.pitches);

  const updatedPitchRequest = usePitchCorrection(
    (state) => state.updatedPitchRequest
  );

  const setSessionSlugRequest = useHubertization(
    (state) => state.setSessionSlugRequest
  );

  const setUpdatedPitchRequest = usePitchCorrection(
    (state) => state.setUpdatedPitchRequest
  );

  const setInitialPitchRequest = useHubertization(
    (state) => state.setInitialPitchRequest
  );

  const [invalidLengthEditModalOpen, setInvalidLengthEditModalOpen] =
    useState(false);

  const vibratoLevelZ = usePitchCorrection((state) => state.vibratoLevel);

  const pitchShiftLevelZ = usePitchCorrection((state) => state.pitchLevel);

  const useKey = useNamedKey((state) => state.getNamedStore(keyNamespace));

  const keyZ = useKey((state) => state.key);

  const sessionSlugRequest = useHubertization(
    (state) => state.sessionSlugRequest
  );

  const initialPitchRequest = useHubertization(
    (state) => state.initialPitchRequest
  );

  const userIsPaid = !isFreeUser(
    user === null ? null : user.stripeSubscriptionId
  );
  const posthog = usePostHog();

  const [pitchCorrectionModalOpen, setPitchCorrectionModalOpen] = useState<{
    originalPitches: Promise<number[]>;
    pitches: Promise<number[]>;
    slug: Promise<string>;
  } | null>(null);

  const selectedFileCover = useConversionInput(
    (state) => state.selectedFileCover
  );

  const urlForTuning =
    (
      selectedFileCover !== null &&
      isSelectedFile(selectedFileCover) &&
      selectedFileCover.selectedSound !== null
    ) ?
      selectedFileCover.selectedSound.presignedUrlPlay
    : null;

  const presignedUrlModelMixedModel: Model["presignedUrlModel"] | null =
    getPresignedUrlModelMixedModel(artistInstrumentModelMixedModel);

  return (
      !(
        selectedFileCover !== null &&
        isSelectedFile(selectedFileCover) &&
        selectedFileCover.progress === null &&
        urlForTuning !== null &&
        urlForTuning !== ""
      ) || disabledExternal
    ) ?
      <Button className={className} disabled={true} onClick={() => {}}>
        {buttonLabel}
      </Button>
    : <>
        {pitchCorrectionModalOpen !== null && (
          <AutotuneModal
            disableDiatonicTranspose={disableDiatonicTranspose}
            namespace={namespace}
            keyNamespace={keyNamespace}
            userIsPaid={userIsPaid}
            signedUrlAudio={
              (
                (selectedFileCover?.selectedSound?.presignedUrlPlay ?? "") ===
                ""
              ) ?
                null
              : (selectedFileCover?.selectedSound?.presignedUrlPlay ?? null)
            }
            signedUrlModel={presignedUrlModelMixedModel}
            open={pitchCorrectionModalOpen !== null}
            sessionSlugGetter={pitchCorrectionModalOpen.slug}
            pitchGetter={pitchCorrectionModalOpen.pitches}
            originalPitchGetter={pitchCorrectionModalOpen.originalPitches}
            closeModal={() => {
              setPitchCorrectionModalOpen(null);
              posthog?.capture("autotuneModalClosed");
            }}
            audioUrl={urlForTuning}
            track={track}
            pitchesCb={() => {}}
          />
        )}

        <InvalidLengthEditModal
          open={invalidLengthEditModalOpen}
          closeModal={() => setInvalidLengthEditModalOpen(false)}
        />
        <Button
          className={className}
          onClick={() => {
            posthog?.capture("autotuneButtonClicked");

            if (
              (selectedFileCover.selectedSound?.lengthS ?? 0) >
              SECONDS_MAX_EDIT_IN_PITCH_EDITOR
            ) {
              setInvalidLengthEditModalOpen(true);
              return;
            }

            // Not awaited: the modal receives the pending request and resolves it itself.
            const applyEditedPitches = async () => {
              const resolvedPitches = await updatedPitchRequest;
              return [
                ...resolvedPitches.slice(0, 300),
                ...pitches.map((i) => (i === 0 ? 0 : semitonesToFrequency(i))),
                ...resolvedPitches.slice(-300),
              ];
            };
            const upr =
              pitches.length > 0 ?
                applyEditedPitches()
              : pitchGetter(selectedFileCover)();

            const ipr = pitches.length > 0 ? initialPitchRequest : upr;

            const ssr =
              pitches.length > 0 ?
                sessionSlugRequest
              : sessionSlugGetter(selectedFileCover)();

            if (pitches.length === 0) {
              setInitialPitchRequest(upr);
            }

            // if the pitches haven't returned yet, make sure to add vibrato and pitch shifting
            const newUpr =
              pitches.length > 0 ?
                upr
              : pitchGetterFollowup(
                  vibratoMiddleware(vibratoLevelZ),
                  pitchShiftMiddleware(pitchShiftLevelZ, keyZ)
                )(upr);

            setUpdatedPitchRequest(newUpr);

            setSessionSlugRequest(ssr);

            setPitchCorrectionModalOpen({
              originalPitches: ipr,
              pitches: newUpr,
              slug: ssr,
            });
          }}
        >
          {buttonLabel}
        </Button>
      </>;
}
